import { FC, useEffect, useRef } from 'react';
import { Report } from 'types/Report';
import PdfExportCoverPage from 'app/modules/public/modules/pdfReports/components/PdfExportCoverPage';
import ReportHeader from 'app/modules/public/modules/pdfReports/modules/insightsPostsPerformance/components/reportPostsPerformance/ReportHeader';
import PageStatistics from 'app/modules/insights/modules/postsPerformance/components/PageStatistics';
import { getMetricsForPageType } from 'app/modules/insights/modules/postsPerformance/utils/metricProperty';
import PostsByMetricSimpleList from 'app/modules/insights/modules/postsPerformance/components/PostsByMetricSimpleList';
import { ReportNoteCard } from 'app/modules/public/modules/pdfReports/modules/customReport/components/ReportNoteCard';
import PagePerformanceSectionExport from 'app/modules/public/modules/pdfReports/modules/customReport/components/PagePerformanceSectionExport';
import { ReportUtils } from 'utils/report';
import dayjs from 'dayjs';
import Container, { InsightsContainerRef } from 'components/insights/Container';
import PageAccessError from 'app/modules/public/modules/pdfReports/components/PageAccessError';
import ReportSectionSkeleton from 'app/modules/public/modules/pdfReports/components/ReportSectionSkeleton';
import { useTranslation } from 'react-i18next';

interface CustomReportPDFExportProps {
  report: Report;
  minimal?: boolean;
  is12hDateFormat?: boolean;
}

/**
 * Signals to the headless PDF renderer (via `body.ready` class) that the
 * insights pages query has finished. Without this signal the renderer's
 * `networkidle0` heuristic can capture the page before the Apollo query
 * fires, producing a PDF whose sections all show `<PageAccessError />`.
 *
 * Companion: the screenshot service waits on `body.ready` when called with
 * `?ready=true`; reports-exporter passes that flag for PDF exports.
 */
const RenderReadySignal: FC<{ arePagesLoading: boolean }> = ({
  arePagesLoading,
}) => {
  useEffect(() => {
    if (!arePagesLoading) {
      document.body.classList.add('ready');
    }
  }, [arePagesLoading]);

  return null;
};

const CustomReportPDFExport: FC<CustomReportPDFExportProps> = ({
  report,
  minimal = false,
  is12hDateFormat = false,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<InsightsContainerRef>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.changeDateFilter({
        startDate: dayjs(report.date_from),
        endDate: dayjs(report.date_to),
      });
    }
  }, [report.date_from, report.date_to]);

  return (
    <Container
      ref={containerRef}
      initialFilterState={{
        startDate: dayjs(report.date_from),
        endDate: dayjs(report.date_to),
      }}
    >
      {({ pages, arePagesLoading }) => (
        <div className={minimal ? 'tw-p-4' : ''}>
          <RenderReadySignal arePagesLoading={arePagesLoading} />
          {!minimal && (
            <div
              style={{ breakInside: 'avoid' }}
              className="tw-min-h-[1137px] tw-rounded-lg tw-bg-gradient-to-b tw-from-[#F8F9FA] tw-to-[#FFF7F9]"
            >
              <PdfExportCoverPage
                title={report.name}
                page={
                  report.profiles?.length === 1
                    ? ReportUtils.findPageByProfile(pages, report.profiles[0])
                    : undefined
                }
                startDate={dayjs(report.date_from)}
                endDate={dayjs(report.date_to)}
                is12hDateFormat={is12hDateFormat}
              />
            </div>
          )}
          {report.profiles?.map((profile) => {
            const page = ReportUtils.findPageByProfile(pages, profile);

            if (arePagesLoading) {
              return <ReportSectionSkeleton key={profile.profile_id} />;
            }

            if (!page)
              return (
                <PageAccessError
                  key={profile.profile_id}
                  profileName={`Profile ID: ${profile.profile_id}`}
                  profileType={profile.profile_type}
                />
              );

            return profile.sections.map((section) => {
              const metrics = Object.values(getMetricsForPageType(page._type))
                .filter((metric) =>
                  (section.metrics ?? []).includes(metric.key),
                )
                .filter((metric) => !metric.deprecated);
              return {
                summary_cards: (
                  <div key={section.id} className={minimal ? 'tw-mb-8' : ''}>
                    <ReportHeader
                      name={report.name}
                      page={page}
                      startDate={dayjs(report.date_from)}
                      endDate={dayjs(report.date_to)}
                      is12hDateFormat={is12hDateFormat}
                      title={t('pdfReports.sections.summaryCards')}
                    />
                    <PageStatistics
                      page={page}
                      startDate={dayjs(report.date_from)}
                      endDate={dayjs(report.date_to)}
                      isExpandingDisabled
                      columns={2}
                    />
                    <ReportNoteCard note={section.note} />
                  </div>
                ),
                page_performance: (
                  <div key={section.id} className={minimal ? 'tw-mb-8' : ''}>
                    <ReportHeader
                      name={report.name}
                      page={page}
                      startDate={dayjs(report.date_from)}
                      endDate={dayjs(report.date_to)}
                      is12hDateFormat={is12hDateFormat}
                      title={t('pdfReports.sections.pagePerformance')}
                    />
                    <PagePerformanceSectionExport
                      page={page}
                      selectedMetrics={section.metrics}
                      view={section.view}
                    />
                    <ReportNoteCard note={section.note} />
                  </div>
                ),
                posts_performance: (
                  <div key={section.id} className={minimal ? 'tw-mb-8' : ''}>
                    <ReportHeader
                      name={report.name}
                      page={page}
                      startDate={dayjs(report.date_from)}
                      endDate={dayjs(report.date_to)}
                      title={t('pdfReports.sections.postsPerformance')}
                    />
                    <div>
                      {metrics
                        .filter((metric) => !metric.deprecated)
                        .map((metric) => (
                          <PostsByMetricSimpleList
                            key={metric.key}
                            page={page}
                            metric={metric}
                            types={[]}
                            startDate={dayjs(report.date_from)}
                            endDate={dayjs(report.date_to)}
                            paginateBy={
                              { top_3: 3, top_6: 6, top_9: 9, top_12: 12 }[
                                section.posts_to_include
                              ]
                            }
                          />
                        ))}
                    </div>
                    <ReportNoteCard note={section.note} />
                  </div>
                ),
              }[section.type];
            });
          })}
        </div>
      )}
    </Container>
  );
};

export default CustomReportPDFExport;
